import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserI, OnlineAgentsI } from '@interfaces/user.interface';
import { ComponentStore } from '@ngrx/component-store';
import { socketStatusT } from '@omnitypes/user.type';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { ListI } from '@interfaces/chat.interface';
import { SystemProfileI } from '@interfaces/system-profile.interface';
import { Store } from '@ngrx/store';

export interface UserState {
  users: UserI[];
  currentUserId: string;
  currentUserStatus: socketStatusT;
  usersOnlineIds: string[];
  currentOnlineUsers: number;
  isPaused: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserStoreService extends ComponentStore<UserState> {
  public users$ = this.select((state) => state.users);
  public isPaused$ = this.select((state) => state.isPaused);
  public listOnlineUsersId$ = this.select((state) => state.usersOnlineIds);
  public selectCurrentUserStatus$ = this.select((state) => state.currentUserStatus);
  public currentOnlineUsers$ = this.select((state) => state.currentOnlineUsers);
  private slug_url = 'users/';

  public onlineUsersSubject = new BehaviorSubject<{ usersOnlineIds: string[]; currentOnlineUsers: number }>({
    usersOnlineIds: [],
    currentOnlineUsers: 0,
  });

  constructor(private readonly usersHelper: UserHelperService, private http: HttpClient, private store: Store) {
    super({
      users: [],
      currentUserId: '',
      currentUserStatus: 'offline',
      usersOnlineIds: [],
      currentOnlineUsers: 0,
      isPaused: false,
    });
  }

  public clearUsersInStore(): void {
    this.setState((state) => ({
      ...state,
      users: [],
    }));
  }

  public updateUser(user: UserI): void {
    this.setState((state) => ({
      ...state,
      users: state.users.map((userItem) => {
        if (userItem._id === user._id) {
          return user;
        }
        return userItem;
      }),
    }));
  }

  public addNewUser(user: UserI): void {
    const userLogged = this.usersHelper.getUserAttributes();
    if (userLogged && userLogged['custom:userId'] === user._id) return;
    this.setState((state) => {
      const hasUserInArray = state.users.map((state_user) => state_user._id).includes(user._id);
      if (!hasUserInArray) {
        const users = [...state.users, user];
        return { ...state, users };
      } else {
        this.updateUser(user);
      }
      return state;
    });
  }

  public addNewUsers(newUsers: UserI[]): void {
    const userLogged = this.usersHelper.getUserAttributes();
    const newUsersFiltered = (newUsers ?? []).filter((newUser) => newUser._id !== userLogged['custom:userId']);
    this.setState((state) => {
      const users = [
        ...(state.users ?? []).filter((user) => !newUsersFiltered.find((newUser) => newUser._id === user._id)),
        ...newUsersFiltered,
      ];
      return {
        ...state,
        users,
      };
    });
  }
  public removeUser(userId: string): void {
    this.setState((state) => {
      const users = state.users.filter((user) => user._id !== userId);
      return { ...state, users };
    });
  }

  public changeActivityStatus(userId: string, activityStatus: socketStatusT): void {
    this.setState((state) => {
      const users = state.users.map((user) => {
        if (user._id === userId) {
          user.activityStatus = activityStatus;
        }
        return user;
      });
      return { ...state, users };
    });
  }

  public setUserAway(userId: string): void {
    this.changeActivityStatus(userId, 'away');
  }

  public setUserBack(userId: string): void {
    this.changeActivityStatus(userId, 'online');
  }

  public changeCurrentUserStatus(status: socketStatusT): void {
    this.setState((state) => {
      return { ...state, currentUserStatus: status };
    });
  }

  public async setUserOnline(userId: string) {
    const currentUsers = await firstValueFrom(this.onlineUsersSubject);
    if (!currentUsers.usersOnlineIds.includes(userId)) {
      const usersOnlineIds = [...currentUsers.usersOnlineIds, userId];
      const currentOnlineUsers = currentUsers.currentOnlineUsers + 1;
      this.onlineUsersSubject.next({ usersOnlineIds, currentOnlineUsers });
    }

    this.setState((state: UserState) => {
      if (!state.usersOnlineIds.includes(userId)) {
        return {
          ...state,
          usersOnlineIds: [...state.usersOnlineIds, userId],
          currentOnlineUsers: state.currentOnlineUsers + 1,
          users: state.users.map((user) => {
            if (user._id === userId) {
              user.activityStatus = 'online';
            }
            return user;
          }),
        };
      }

      return state;
    });
  }

  public async setUserOffline(userId: string) {
    const currentUsers = await firstValueFrom(this.onlineUsersSubject);
    if (currentUsers.usersOnlineIds.includes(userId)) {
      const usersOnlineIds = currentUsers.usersOnlineIds.filter((id) => id !== userId);
      const currentOnlineUsers = currentUsers.currentOnlineUsers - 1;
      this.onlineUsersSubject.next({ usersOnlineIds, currentOnlineUsers });
    }
    this.setState((state: UserState) => {
      if (state.usersOnlineIds.includes(userId)) {
        return {
          ...state,
          usersOnlineIds: state.usersOnlineIds.filter((id) => id !== userId),
          currentOnlineUsers: state.currentOnlineUsers - 1,
          users: state.users.map((user) => {
            if (user._id === userId) {
              user.activityStatus = 'offline';
            }
            return user;
          }),
        };
      }
      return state;
    });
  }

  public setUsersOnline({
    onlineUsers,
    currentOnlineUsers,
  }: {
    onlineUsers: OnlineAgentsI[];
    currentOnlineUsers: number;
  }): void {
    const usersOnlineIds = onlineUsers.map((user) => user._id);
    this.onlineUsersSubject.next({
      usersOnlineIds,
      currentOnlineUsers,
    });

    this.setState((state: UserState) => {
      const users = (state.users ?? []).map((user) => {
        return {
          ...user,
          activityStatus: usersOnlineIds.includes(user._id) ? 'online' : 'offline',
        } as UserI;
      });
      return { ...state, usersOnlineIds, currentOnlineUsers, users };
    });
  }

  public getUsersOnline() {
    return this.onlineUsersSubject.asObservable();
  }

  public addIdInUsersOnlineIds(userId: string): void {
    this.setState((state) => {
      const hasUserInArray = state.usersOnlineIds.includes(userId);
      if (!hasUserInArray) {
        const usersOnlineIds = [...state.usersOnlineIds, userId];
        return { ...state, usersOnlineIds };
      }
      return state;
    });
  }

  public removeIdInUsersOnlineIds(userId: string): void {
    this.setState((state) => {
      const usersOnlineIds = state.usersOnlineIds.filter((id) => id !== userId);
      return { ...state, usersOnlineIds };
    });
  }

  private sortByName(users: UserI[]): UserI[] {
    return users.sort((a, b) => {
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 0;
    });
  }

  public getUserById(userId: string): Observable<UserI | undefined> {
    try {
      return this.http.get<UserI>(environment.OMNISMART_API_USERS + this.slug_url + userId);
    } catch (error) {
      throw new Error(`UserStoreService getUserById error: ${error}`);
    }
  }

  public getAllUsers(search: string = ''): Observable<ListI[]> {
    return this.selectUsers(search).pipe(this.mapUsers()) as Observable<ListI[]>;
  }

  private mapUsers() {
    return map((users: UserI[]) =>
      users.map((user: UserI) => {
        const profileLevel = (user.profiles as SystemProfileI[]).reduce((acc, profile) => {
          if (!profile.level) {
            return acc;
          }

          return acc < profile.level ? profile.level : acc;
        }, 0);
        return {
          name: user.name || user.email,
          _id: user._id,
          avatar: user?.photo || null,
          teams: user.teams,
          profiles: user.profiles.map((profile) => profile.name).join(', '),
          profileLevel,
          activityStatus: user.activityStatus,
          ramalNumber: user.ramalNumber,
        };
      })
    );
  }

  public getOnlineUsers(search: string = ''): Observable<ListI[]> {
    return this.selectUserOnline(search).pipe(this.mapUsers()) as Observable<ListI[]>;
  }

  public getOfflineUsers(search: string = ''): Observable<ListI[]> {
    return this.selectUserOffline(search).pipe(this.mapUsers()) as Observable<ListI[]>;
  }

  private readonly selectUserOnline = (search = '') => {
    const regex = new RegExp(`^${search.toLocaleLowerCase()}`, 'i');
    return this.select((state) => state.users).pipe(
      map((users) => {
        let usersOnline = users.filter(
          (user) =>
            regex.test(user.name.toLocaleLowerCase() || user.email.toLocaleLowerCase()) &&
            user.activityStatus === 'online'
        );
        usersOnline = this.sortByName(usersOnline);
        return usersOnline;
      })
    );
  };

  private readonly selectUsers = (search = '') => {
    const regex = new RegExp(`^${search.toLocaleLowerCase()}`, 'i');
    return this.select((state) => state.users).pipe(
      map((users) => {
        let filtered = users.filter((user) =>
          regex.test(user.name.toLocaleLowerCase() || user.email.toLocaleLowerCase())
        );
        filtered = this.sortByName(filtered);
        return filtered;
      })
    );
  };

  private readonly selectUserOffline = (search = ''): Observable<UserI[]> => {
    const regex = new RegExp(`^${search.toLocaleLowerCase()}`, 'i');
    return this.select((state) => state.users).pipe(
      map((users) => {
        let usersOffline = users.filter(
          (user) =>
            regex.test(user.name.toLocaleLowerCase() || user.email.toLocaleLowerCase()) &&
            user.activityStatus === 'offline'
        );
        usersOffline = this.sortByName(usersOffline);
        return usersOffline;
      })
    );
  };

  public getUsers(): Observable<UserI[]> {
    return this.users$;
  }

  public changePause(status: boolean) {
    this.setState((state) => ({ ...state, isPaused: status }));
  }
}
